import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { galleryApi } from '../../api/endpoints'
import { Lightbox } from '../common/Lightbox'
import type { MediaCategory } from '../../types/api'

/**
 * A row of the owner's photographs, on any page that has something to show.
 *
 * <p>The food page described the kitchen and showed nothing of it; the activities page listed
 * mushroom foraging with no mushrooms. Several hundred photographs were sitting in the library
 * one page away. This puts the relevant ones where the words are.
 *
 * <p>It asks the gallery for one subject and takes the first few. Nothing is hardcoded, so a
 * picture the owner uploads tonight is on the food page tomorrow without a deploy - and a
 * picture she hides disappears from here too.
 */
export function PhotoStrip({
  category,
  title,
  count = 6,
  showAllLink = true,
  kind,
}: {
  category?: MediaCategory
  title?: string
  count?: number
  showAllLink?: boolean
  /** VIDEO for a row of films. Omitted for everything, which is the usual case. */
  kind?: 'IMAGE' | 'VIDEO'
}) {
  const { t } = useTranslation()
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const page = useQuery({
    queryKey: ['gallery', 'strip', category, count, kind],
    queryFn: () => galleryApi.page(category, 0, count, kind),
  })

  const items = page.data?.items ?? []

  // Nothing to show is not an error and not an empty box: the section simply is not there.
  if (!page.isPending && items.length === 0) return null

  const move = (delta: number) =>
    setOpenIndex((current) => {
      if (current === null) return current
      const next = current + delta
      return next < 0 || next >= items.length ? current : next
    })

  return (
    <Box>
      {title && (
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="h3">{title}</Typography>
          {showAllLink && (
            <Button
              component={RouterLink}
              to="/gallery"
              endIcon={<ArrowForwardIcon />}
              size="small"
            >
              {t('gallery.seeAll')}
            </Button>
          )}
        </Stack>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: `repeat(${Math.min(count, 6)}, 1fr)`,
          },
          gap: 1.5,
        }}
      >
        {page.isPending &&
          Array.from({ length: count }).map((_, index) => (
            <Skeleton key={index} variant="rounded" sx={{ aspectRatio: '1 / 1' }} />
          ))}

        {items.map((item, index) => (
          <Box
            key={item.url}
            onClick={() => setOpenIndex(index)}
            sx={{
              position: 'relative',
              aspectRatio: '1 / 1',
              overflow: 'hidden',
              borderRadius: 3,
              cursor: 'zoom-in',
              bgcolor: 'action.hover',
              '&:hover img': { transform: 'scale(1.05)' },
            }}
          >
            {item.kind === 'VIDEO' ? (
              <>
                <Box
                  component="video"
                  src={item.url}
                  muted
                  playsInline
                  // Films here are 20 to 90 MB. Nothing is fetched until somebody opens one.
                  preload="none"
                  poster={item.thumbnailUrl ?? undefined}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', bgcolor: 'grey.900' }}
                />
                <PlayCircleIcon
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: 44,
                    color: 'common.white',
                    filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
                    pointerEvents: 'none',
                  }}
                />
              </>
            ) : (
              <Box
                component="img"
                src={item.thumbnailUrl ?? item.url}
                alt={item.altText ?? ''}
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 400ms ease',
                }}
              />
            )}
          </Box>
        ))}
      </Box>

      <Lightbox
        items={items}
        index={openIndex}
        onClose={() => setOpenIndex(null)}
        onMove={move}
      />
    </Box>
  )
}
